import React from 'react';
import { compose } from 'redux';
import { withRouter, RouteComponentProps } from 'react-router';
import { withTranslation, WithTranslation } from 'react-i18next';
import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';
import { withMobileContext } from '@wix/da-shared-react/pkg/Context/MobileContext';
import OnScrollBottom, {
  DISTANCE_UNIT,
} from '@wix/da-shared-react/pkg/OnScrollBottom';
import { IconSize } from '@wix/da-shared-react/pkg/Icons/IconWrap';
import FoldersHeader from '../FoldersHeader';
import FolderSlider from '../FolderSlider';
import StickyFolderBar from '../FolderBar/StickyFolderBar';
import FolderContentList from '../FolderContentList';
import FolderBarFolderDropdown from '../FolderBar/_partials/FolderBarFolderDropdown';
import WidgetHeaderMobile from '../../../UserWidgets/_partials/WidgetHeaderMobile';
import { PapiGallection } from '@wix/da-types-papi';

import s from './GallectionsListView.scss';

const DISTANCE_TO_RENDER_NEXT_FOLDER = 2; // 2 window heights

export type Props = {
  isMobile?: boolean;
  folders: PapiGallection[];
  currentFolder?: PapiGallection;
  fullViewFolderIds: number[];
  hasMoreDeviationsInLastFullViewFolder: boolean;
  isFetchingPausedInLastFullViewFolder: boolean;
  isRedesigned?: boolean;
  setCurrentlyViewedFolderId: (folderId: number) => void;
  addFolderToFullViewList: (folderId: number) => void;
};

class GallectionsListView extends React.Component<
  Props & WithTranslation & RouteComponentProps<any>
> {
  static displayName = 'GallectionsListView';

  componentDidMount() {
    const {
      folders,
      match: {
        params: { folderId },
      },
    } = this.props;
    if (folderId && folders && folders.length) {
      const intFolderId = +folderId;
      const { parentId = null } =
        folders.filter(item => item.folderId === intFolderId)[0] || {};
      parentId && this.scrollToSubFolder();
    }
  }

  render() {
    const { isMobile, isRedesigned } = this.props;
    return (
      <div className={s['root']}>
        <ErrorBoundary>
          {!isMobile && <FoldersHeader />}
          {isMobile && this.renderGallectionsDropdown()}
          <FolderSlider />
          <div id="sub-folder-gallery">
            {!isMobile && !isRedesigned && <StickyFolderBar />}
            <FolderContentList onScrollToFolder={this.onScrollToFolder} />
            <OnScrollBottom
              distanceToBottom={DISTANCE_TO_RENDER_NEXT_FOLDER}
              distanceUnit={DISTANCE_UNIT.WINDOW_HEIGHT}
              onScrolledToBottom={this.addNextFolderToFullViewList}
            />
          </div>
        </ErrorBoundary>
      </div>
    );
  }

  renderGallectionsDropdown() {
    return (
      <WidgetHeaderMobile
        header={false}
        isSticky
        extras={
          <FolderBarFolderDropdown
            currentFolder={this.props.currentFolder}
            arrowIconSize={IconSize.MEDIUM}
          />
        }
      />
    );
  }

  onScrollToFolder = (folderId: number) => {
    const { currentFolder, setCurrentlyViewedFolderId } = this.props;
    if (folderId !== currentFolder?.folderId) {
      setCurrentlyViewedFolderId(folderId);
    }
  };

  scrollToSubFolder = () => {
    if (typeof document !== 'undefined') {
      const anchor = document.getElementById('sub-folder-gallery');
      anchor && anchor.scrollIntoView();
    }
  };

  addNextFolderToFullViewList = () => {
    const {
      folders,
      fullViewFolderIds,
      hasMoreDeviationsInLastFullViewFolder,
      isFetchingPausedInLastFullViewFolder,
      addFolderToFullViewList,
    } = this.props;
    if (
      hasMoreDeviationsInLastFullViewFolder &&
      !isFetchingPausedInLastFullViewFolder
    ) {
      // we load next folder only when we run out deviations to load in the last one
      // or we paused load-more because the user clicked jump to comments
      return;
    }
    if (fullViewFolderIds.length === folders.length) {
      return;
    }

    const topLevelFolders = folders.filter(folder => !folder.parentId);

    const nextFolder = this.getNextFolder(topLevelFolders);

    if (nextFolder) {
      addFolderToFullViewList(nextFolder.folderId);
    }
  };

  getNextFolder = folders => {
    const { fullViewFolderIds } = this.props;

    const lastFullViewFolderId =
      fullViewFolderIds[fullViewFolderIds.length - 1];

    return folders[
      folders.findIndex(folder => folder.folderId === lastFullViewFolderId) + 1
    ];
  };
}

const enhance = compose(withRouter, withTranslation(), withMobileContext);

export default enhance(GallectionsListView) as React.ComponentType<Props>;
